// components/ActivitySummary.js
import React, { useState, useMemo } from "react";
import { Card, Badge } from "react-bootstrap";

const dateToString = (d) => {
  if (!d || isNaN(d.getTime())) return 'N/A';
  return `${d.getHours()}:${d.getMinutes()}:${d.getSeconds()}`;
};

/**
 * Modern Activity Summary - Execution history table
 * Dark theme with professional styling matching TradingCommand UI
 * Memoized to prevent unnecessary re-renders
 */
const ActivitySummary = React.memo(({ data }) => {
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('desc');

  // Debug: Log first item to see data structure
  React.useEffect(() => {
    if (data && data.length > 0) {
      console.log("ActivitySummary received data, first item:", data[0]);
    }
  }, [data]);

  // Create stable key for data to prevent flashing
  const dataKey = useMemo(() => {
    if (!data || data.length === 0) return '';
    return data.map(d => `${d.a_id}-${d.pl}-${d.pl_delta}`).join('|');
  }, [data]);

  // Calculate slippage for each activity - memoized with stable key
  const enrichedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    return data.map((info) => {
      // Safely parse numeric values
      const fillPrice = parseFloat(info.price) || 0;
      const limitPrice = parseFloat(info.limit) || 0;
      const slippage = fillPrice && limitPrice ? fillPrice - limitPrice : 0;
      const slippagePercent = limitPrice ? ((slippage / limitPrice) * 100) : 0;
      
      // Ensure all fields exist with defaults
      return {
        group: info.group || 0,
        symbol: info.symbol || 'N/A',
        date: info.date || '',
        created_at_gmt: info.created_at_gmt || null,
        submitted_at_gmt: info.submitted_at_gmt || null,
        type: info.type || '',
        side: info.side || '',
        qty: info.qty || 0,
        open_qty: info.open_qty || 0,
        pl_delta: info.pl_delta || '0.00',
        tax: info.tax || '0.00',
        pl: info.pl || '0.00',
        a_id: info.a_id || info.id,
        fillPrice,
        limitPrice,
        slippage,
        slippagePercent,
        ...info // Keep all original fields
      };
    });
  }, [dataKey]);

  // Sorting logic
  const sortedData = useMemo(() => {
    if (!sortField) return enrichedData;

    return [...enrichedData].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle numeric fields
      if (typeof aVal === 'string' && !isNaN(aVal)) aVal = parseFloat(aVal);
      if (typeof bVal === 'string' && !isNaN(bVal)) bVal = parseFloat(bVal);

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [enrichedData, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Format currency
  const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) return '$0.00';
    return `$${parseFloat(value).toFixed(2)}`;
  };

  // Get badge variant for side
  const getSideBadge = (side) => {
    const sideUpper = side?.toUpperCase();
    if (sideUpper === 'BUY') return { bg: 'success', text: 'LONG' };
    if (sideUpper === 'SELL') return { bg: 'danger', text: 'SHORT' };
    return { bg: 'secondary', text: side };
  };

  // Get slippage quality color
  const getSlippageColor = (slippagePercent) => {
    const absSlippage = Math.abs(slippagePercent);
    if (absSlippage <= 0.01) return '#10b981'; // Excellent - green
    if (absSlippage <= 0.05) return '#3b82f6'; // Good - blue
    if (absSlippage <= 0.1) return '#fbbf24'; // Fair - yellow
    return '#ef4444'; // Poor - red
  };

  if (!data || data.length === 0) {
    return (
      <Card style={{ 
        backgroundColor: '#1a1a1a', 
        border: '1px solid #333',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.4)'
      }}>
        <Card.Body style={{ padding: '20px', textAlign: 'center', color: '#888' }}>
          No execution data available
        </Card.Body>
      </Card>
    );
  }

  

  return (
    <Card style={{ 
      backgroundColor: '#1a1a1a', 
      border: '1px solid #333',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.4)'
    }}>
      <Card.Header style={{ 
        backgroundColor: '#2a2a2a', 
        borderBottom: '1px solid #444',
        padding: '12px 16px'
      }}>
        <div className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0" style={{ color: '#e0e0e0', fontSize: '14px', fontWeight: '600' }}>
            📊 Execution History
          </h6>
          <Badge bg="dark" style={{ border: '1px solid #555', fontSize: '11px' }}>
            {data.length} Executions
          </Badge>
        </div>
      </Card.Header>

      <Card.Body style={{ padding: 0, backgroundColor: '#1a1a1a' }}>
        <div style={{ 
          maxHeight: "500px", 
          overflowY: "auto",
          overflowX: "auto"
        }}>
          <table style={{ 
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: 0,
            fontSize: '12px'
          }}>
            <thead style={{ 
              position: 'sticky', 
              top: 0, 
              backgroundColor: '#2a2a2a',
              zIndex: 10,
              borderBottom: '2px solid #444'
            }}>
              <tr>
                <th 
                  onClick={() => handleSort('group')}
                  style={{
                    padding: '12px 8px',
                    textAlign: 'center',
                    color: '#888',
                    fontWeight: '600',
                    fontSize: '11px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    borderRight: '1px solid #333',
                    whiteSpace: 'nowrap'
                  }}
                >
                  TRADE<br/>GROUP {sortField === 'group' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  onClick={() => handleSort('date')}
                  style={{
                    padding: '12px 8px',
                    textAlign: 'center',
                    color: '#888',
                    fontWeight: '600',
                    fontSize: '11px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    borderRight: '1px solid #333',
                    whiteSpace: 'nowrap'
                  }}
                >
                  FILLED<br/>AT {sortField === 'date' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  style={{
                    padding: '12px 8px',
                    textAlign: 'center',
                    color: '#888',
                    fontWeight: '600',
                    fontSize: '11px',
                    borderRight: '1px solid #333',
                    whiteSpace: 'nowrap'
                  }}
                >
                  CREATED<br/>AT
                </th>
                <th 
                  style={{
                    padding: '12px 8px',
                    textAlign: 'center',
                    color: '#888',
                    fontWeight: '600',
                    fontSize: '11px',
                    borderRight: '1px solid #333',
                    whiteSpace: 'nowrap'
                  }}
                >
                  SUBMITTED<br/>AT
                </th>
                <th 
                  onClick={() => handleSort('symbol')}
                  style={{
                    padding: '12px 8px',
                    textAlign: 'center',
                    color: '#888',
                    fontWeight: '600',
                    fontSize: '11px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    borderRight: '1px solid #333',
                    whiteSpace: 'nowrap'
                  }}
                >
                  SYMBOL {sortField === 'symbol' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  style={{
                    padding: '12px 8px',
                    textAlign: 'center',
                    color: '#888',
                    fontWeight: '600',
                    fontSize: '11px',
                    borderRight: '1px solid #333',
                    whiteSpace: 'nowrap'
                  }}
                >
                  TYPE
                </th>
                <th 
                  onClick={() => handleSort('side')}
                  style={{
                    padding: '12px 8px',
                    textAlign: 'center',
                    color: '#888',
                    fontWeight: '600',
                    fontSize: '11px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    borderRight: '1px solid #333',
                    whiteSpace: 'nowrap'
                  }}
                >
                  SIDE {sortField === 'side' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  style={{
                    padding: '12px 8px',
                    textAlign: 'center',
                    color: '#888',
                    fontWeight: '600',
                    fontSize: '11px',
                    borderRight: '1px solid #333',
                    whiteSpace: 'nowrap'
                  }}
                >
                  PRICE<br/>(FILL/LIMIT)
                </th>
                <th 
                  style={{
                    padding: '12px 8px',
                    textAlign: 'center',
                    color: '#888',
                    fontWeight: '600',
                    fontSize: '11px',
                    borderRight: '1px solid #333',
                    whiteSpace: 'nowrap'
                  }}
                >
                  SLIPPAGE
                </th>
                <th 
                  onClick={() => handleSort('qty')}
                  style={{
                    padding: '12px 8px',
                    textAlign: 'center',
                    color: '#888',
                    fontWeight: '600',
                    fontSize: '11px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    borderRight: '1px solid #333',
                    whiteSpace: 'nowrap'
                  }}
                >
                  TRADE<br/>QTY {sortField === 'qty' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  style={{
                    padding: '12px 8px',
                    textAlign: 'center',
                    color: '#888',
                    fontWeight: '600',
                    fontSize: '11px',
                    borderRight: '1px solid #333',
                    whiteSpace: 'nowrap'
                  }}
                >
                  OPEN<br/>POSITIONS
                </th>
                <th 
                  onClick={() => handleSort('pl_delta')}
                  style={{
                    padding: '12px 8px',
                    textAlign: 'center',
                    color: '#888',
                    fontWeight: '600',
                    fontSize: '11px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    borderRight: '1px solid #333',
                    whiteSpace: 'nowrap'
                  }}
                >
                  GROUP<br/>P&L {sortField === 'pl_delta' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  onClick={() => handleSort('tax')}
                  style={{
                    padding: '12px 8px',
                    textAlign: 'center',
                    color: '#888',
                    fontWeight: '600',
                    fontSize: '11px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    borderRight: '1px solid #333',
                    whiteSpace: 'nowrap'
                  }}
                >
                  SEC<br/>FEES {sortField === 'tax' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  onClick={() => handleSort('pl')}
                  style={{
                    padding: '12px 8px',
                    textAlign: 'center',
                    color: '#888',
                    fontWeight: '600',
                    fontSize: '11px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    whiteSpace: 'nowrap'
                  }}
                >
                  TOTAL<br/>P&L {sortField === 'pl' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedData.reverse().map((info, index) => {
                const sideBadge = getSideBadge(info.side);
                const isPositivePL = parseFloat(info.pl || 0) >= 0;
                const slippageColor = getSlippageColor(info.slippagePercent);
                
                return (
                  <tr 
                    key={info.a_id || index}
                    style={{
                      backgroundColor: index % 2 === 0 ? '#1a1a1a' : '#222',
                      borderBottom: '1px solid #333',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2a2a2a'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#1a1a1a' : '#222'}
                  >
                    <td style={{ 
                      padding: '10px 8px', 
                      textAlign: 'center',
                      color: '#e0e0e0',
                      borderRight: '1px solid #333',
                      fontWeight: '600'
                    }}>
                      {info.group}
                    </td>
                    <td style={{ 
                      padding: '10px 8px', 
                      textAlign: 'center',
                      color: '#aaa',
                      borderRight: '1px solid #333',
                      fontSize: '11px'
                    }}>
                      {info.date}
                    </td>
                    <td style={{ 
                      padding: '10px 8px', 
                      textAlign: 'center',
                      color: '#aaa',
                      borderRight: '1px solid #333',
                      fontSize: '11px'
                    }}>
                      {info.created_at_gmt ? dateToString(new Date(info.created_at_gmt)) : 'N/A'}
                    </td>
                    <td style={{ 
                      padding: '10px 8px', 
                      textAlign: 'center',
                      color: '#aaa',
                      borderRight: '1px solid #333',
                      fontSize: '11px'
                    }}>
                      {info.submitted_at_gmt ? dateToString(new Date(info.submitted_at_gmt)) : 'N/A'}
                    </td>
                    <td style={{ 
                      padding: '10px 8px', 
                      textAlign: 'center',
                      color: '#fff',
                      borderRight: '1px solid #333',
                      fontWeight: '600',
                      fontSize: '13px'
                    }}>
                      {info.symbol}
                    </td>
                    <td style={{ 
                      padding: '10px 8px', 
                      textAlign: 'center',
                      color: '#aaa',
                      borderRight: '1px solid #333',
                      fontSize: '11px',
                      textTransform: 'uppercase'
                    }}>
                      {info.type}
                    </td>
                    <td style={{ 
                      padding: '10px 8px', 
                      textAlign: 'center',
                      borderRight: '1px solid #333'
                    }}>
                      <Badge 
                        bg={sideBadge.bg}
                        style={{ fontSize: '10px', fontWeight: '600' }}
                      >
                        {sideBadge.text}
                      </Badge>
                    </td>
                    <td style={{ 
                      padding: '10px 8px', 
                      textAlign: 'center',
                      color: '#e0e0e0',
                      borderRight: '1px solid #333',
                      fontSize: '11px',
                      lineHeight: '1.5'
                    }}>
                      <div style={{ fontWeight: '600', color: '#fff' }}>
                        {formatCurrency(info.fillPrice)}
                      </div>
                      <div style={{ color: '#888', fontSize: '10px' }}>
                        limit: {formatCurrency(info.limitPrice)}
                      </div>
                    </td>
                    <td style={{ 
                      padding: '10px 8px', 
                      textAlign: 'center',
                      borderRight: '1px solid #333'
                    }}>
                      {info.slippage !== 0 ? (
                        <div style={{ fontSize: '11px' }}>
                          <div style={{ 
                            color: slippageColor,
                            fontWeight: '600'
                          }}>
                            {info.slippage > 0 ? '+' : ''}{formatCurrency(info.slippage)}
                          </div>
                          <div style={{ 
                            color: slippageColor,
                            fontSize: '10px'
                          }}>
                            ({info.slippagePercent > 0 ? '+' : ''}{info.slippagePercent.toFixed(2)}%)
                          </div>
                        </div>
                      ) : (
                        <span style={{ color: '#10b981', fontSize: '11px' }}>✓ Perfect</span>
                      )}
                    </td>
                    <td style={{ 
                      padding: '10px 8px', 
                      textAlign: 'center',
                      color: '#e0e0e0',
                      borderRight: '1px solid #333',
                      fontWeight: '600'
                    }}>
                      {info.qty}
                    </td>
                    <td style={{ 
                      padding: '10px 8px', 
                      textAlign: 'center',
                      color: '#e0e0e0',
                      borderRight: '1px solid #333',
                      fontWeight: '600'
                    }}>
                      {info.open_qty}
                    </td>
                    <td style={{ 
                      padding: '10px 8px', 
                      textAlign: 'center',
                      color: parseFloat(info.pl_delta) >= 0 ? '#10b981' : '#ef4444',
                      borderRight: '1px solid #333',
                      fontWeight: '600'
                    }}>
                      {formatCurrency(info.pl_delta)}
                    </td>
                    <td style={{ 
                      padding: '10px 8px', 
                      textAlign: 'center',
                      color: '#ef4444',
                      borderRight: '1px solid #333',
                      fontWeight: '600'
                    }}>
                      {formatCurrency(info.tax)}
                    </td>
                    <td style={{ 
                      padding: '10px 8px', 
                      textAlign: 'center',
                      color: isPositivePL ? '#10b981' : '#ef4444',
                      fontWeight: '700',
                      fontSize: '13px'
                    }}>
                      {formatCurrency(info.pl)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Summary Footer */}
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#2a2a2a',
          borderTop: '2px solid #444',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ fontSize: '11px', color: '#888' }}>
            Click column headers to sort
          </div>
          <div style={{ fontSize: '11px', color: '#888' }}>
            Total Executions: <strong style={{ color: '#e0e0e0' }}>{data.length}</strong>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
});

export default ActivitySummary;